import { useEffect, useState } from "react";
import { Box, Typography, Chip, CircularProgress, alpha, useTheme } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { getMessages } from "./api/newTelemetry";
import { auditParams, fetchParams, hasParams } from "./params";

/**
 * Failsafe and arming parameters against the golden set for this airframe.
 * `onAudit` hands the result up so the RCA export can include it.
 */
const LogParamAudit = ({ log, fetchMessages = getMessages, onAudit }) => {
  const theme = useTheme();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!log?.id || !hasParams(log)) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchParams(log.id, fetchMessages)
      .then((params) => {
        if (cancelled) return;
        const result = auditParams(log, params);
        setAudit(result);
        onAudit?.(result);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // onAudit is a page-level setter; excluding it keeps this to one fetch per log.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log, fetchMessages]);

  if (!hasParams(log)) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        This log carries no PARM messages, so the parameters it flew with can't be audited.
      </Typography>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress color="secondary" size={18} />
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Reading parameters…
        </Typography>
      </Box>
    );
  }
  if (error || !audit) {
    return (
      <Typography variant="body2" color="error">
        Could not read parameters: {error || "no data"}
      </Typography>
    );
  }

  const toneFor = (severity) =>
    severity === "critical" ? theme.palette.error.main : theme.palette.warning.main;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
        {audit.checked} safety-critical parameters checked against the{" "}
        <b>{audit.profile || "generic"}</b> golden set — {audit.matched} match.
      </Typography>

      {audit.truncated && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }}>
          The parameter series was downsampled, so a parameter absent here may just be a dropped sample.
          Missing-parameter checks were skipped.
        </Typography>
      )}

      {audit.deviations.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            No deviations from the golden set.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {audit.deviations.map((d) => {
            const tone = toneFor(d.severity);
            const Icon = d.severity === "critical" ? ErrorOutlineOutlinedIcon : WarningAmberOutlinedIcon;
            return (
              <Box
                key={d.name}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  p: 1.75,
                  borderRadius: 2,
                  border: `1px solid ${alpha(tone, 0.35)}`,
                  backgroundColor: alpha(tone, 0.06),
                }}
              >
                <Icon sx={{ fontSize: 20, color: tone, mt: 0.25 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                      {d.name}
                    </Typography>
                    <Chip
                      label={d.severity}
                      size="small"
                      sx={{ height: 20, fontSize: 11, color: tone, backgroundColor: alpha(tone, 0.14) }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.primary }}>
                    Flew with <b>{d.value}</b>, expected <b>{d.expected}</b>.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, color: theme.palette.text.secondary }}>
                    {d.why}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {audit.missing.length > 0 && (
        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: theme.palette.text.secondary }}>
          Not present in this log: {audit.missing.map((m) => m.name).join(", ")}. Parameter names differ
          between firmware versions and vehicle types — confirm against this build before treating any of
          them as unconfigured.
        </Typography>
      )}
    </Box>
  );
};

export default LogParamAudit;
